import React, { createContext, useContext, useState, useCallback } from 'react';

interface SharedElementPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface NavigationPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface SharedElementContextType {
  activeElementId: string | null;
  elementPosition: SharedElementPosition | null;
  navigationPosition: NavigationPosition | null;
  isTransitionActive: boolean;
  registerElement: (id: string, rect: DOMRect) => void;
  openElement: (id: string) => void;
  closeElement: () => void;
  registerNavigationPosition: (position: NavigationPosition) => void;
  setTransitionActive: (active: boolean) => void;
}

const SharedElementContext = createContext<SharedElementContextType | null>(null);

export function SharedElementProvider({ children }: { children: React.ReactNode }) {
  const [activeElementId, setActiveElementId] = useState<string | null>(null);
  const [elementPosition, setElementPosition] = useState<SharedElementPosition | null>(null);
  const [navigationPosition, setNavigationPosition] = useState<NavigationPosition | null>(null);
  const [isTransitionActive, setIsTransitionActive] = useState(false);
  const [elementPositions] = useState<Map<string, SharedElementPosition>>(new Map());

  const registerElement = useCallback((id: string, rect: DOMRect) => {
    elementPositions.set(id, {
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, [elementPositions]);

  const openElement = useCallback((id: string) => {
    const position = elementPositions.get(id);
    if (position) {
      setElementPosition(position);
      setActiveElementId(id);
    }
  }, [elementPositions]);

  const closeElement = useCallback(() => {
    setActiveElementId(null);
    setTimeout(() => setElementPosition(null), 600);
  }, []);

  const registerNavigationPosition = useCallback((position: NavigationPosition) => {
    setNavigationPosition(position);
  }, []);

  const setTransitionActive = useCallback((active: boolean) => {
    setIsTransitionActive(active);
  }, []);

  return (
    <SharedElementContext.Provider
      value={{
        activeElementId,
        elementPosition,
        navigationPosition,
        isTransitionActive,
        registerElement,
        openElement,
        closeElement,
        registerNavigationPosition,
        setTransitionActive,
      }}
    >
      {children}
    </SharedElementContext.Provider>
  );
}

export function useSharedElement() {
  const context = useContext(SharedElementContext);
  if (!context) {
    throw new Error('useSharedElement must be used within SharedElementProvider');
  }
  return context;
}
